import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import TitleAndBack from 'components/v2/TitleAndBack';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { Skeleton } from 'primereact/skeleton';
import EpdRoutes from 'routes/EpdRoutes';
import { useTool, useToolVerifiers } from 'services/api/adminToolsQueriesAndMutations';
import { WizardHeaderButtonBox, WizardHeaderGrid } from 'styles/v2/Styles.styled';
import AdminMainContainer from 'components/admin/search/AdminMainContainer';
import AdminEditToolHead from './AdminEditToolHead';
import AdminEditToolVersion from './AdminEditToolVersion';
import AdminEditToolAddVersionButton from './AdminEditToolAddVersionButton';
import AdminEditToolActionButtons from './AdminEditToolActionButtons';
import { useAdminLookup } from 'services/api/queries';
import { AdminLookupKeyEnum } from 'types/types';
import withAxiosErrorBoundary from './utils/withAxiosErrorBoundary';
import getErrorsLikeFormik from './utils/getErrorsLikeFormik';
import { toolHeadSchema } from './utils/toolValidationSchemas';
import AdminEditToolHeadSkeleton from './AdminEditToolHeadSkeleton';

const AdminEditTool: React.FC<RouteComponentProps<{ id: string }>> = ({
  match: {
    params: { id },
  },
  history,
}) => {
  const toolData = useTool(id).data;
  const countries = useAdminLookup(AdminLookupKeyEnum.COUNTRIES).data;
  const toolVerifiers = useToolVerifiers().data;

  const isLoading = !toolData || !countries || !toolVerifiers;
  if (isLoading) {
    return (
      <AdminMainContainer>
        <WizardHeaderGrid>
          <TitleAndBack
            title={<Skeleton width="311px" height="34px" />}
            backTo="Back to tools"
            onBack={() => history.push(EpdRoutes.admin.tools.search)}
          />
        </WizardHeaderGrid>

        <AdminEditToolHeadSkeleton />
      </AdminMainContainer>
    );
  }

  const headErrors = getErrorsLikeFormik(toolHeadSchema, toolData);
  const hasHeadErrors = Object.keys(headErrors).length > 0;

  return (
    <AdminMainContainer>
      <ConfirmDialog />

      <WizardHeaderGrid>
        <TitleAndBack
          title={toolData.name.trim().length === 0 ? 'Unnamed tool' : toolData.name}
          backTo="Back to tools"
          onBack={() => history.push(EpdRoutes.admin.tools.search)}
        />

        <WizardHeaderButtonBox>
          <AdminEditToolActionButtons toolData={toolData} />
        </WizardHeaderButtonBox>
      </WizardHeaderGrid>

      <AdminEditToolHead toolData={toolData} errors={headErrors} countries={countries} />

      <AdminEditToolAddVersionButton toolId={toolData.id} />

      {toolData.versions.map((v, i) => {
        if (v === 'skeleton') {
          return <Skeleton key={`s${i}`} height="50rem"  className="mt-4" />;
        }
        return (
          <AdminEditToolVersion
            key={v.ord}
            toolData={toolData}
            versionData={v}
            hasHeadErrors={hasHeadErrors}
            toolVerifiers={toolVerifiers}
          />
        );
      })}
    </AdminMainContainer>
  );
};

export default withAxiosErrorBoundary(withGreyBackground(AdminEditTool));
